import { useContext, useState } from "react";
import { Avatar, Box, Drawer, IconButton, Typography } from "@mui/material";
import { Menu, Logout, SpaceDashboard, Storefront, LocalOffer, EmojiEvents, QrCode2, QrCodeScanner, PeopleAlt, ConfirmationNumber, Redeem, PointOfSale, CreditCard, RateReview, AccountBalanceWallet, Groups, Category, Map, Tune, Image, Print, Settings, History, Campaign } from "@mui/icons-material";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { DataContext } from "../../context/DataContext";
import { consoleColors, headingCase } from "./consoleTheme";
import AdminHome from "./AdminHome";
import StoreMaster, { StoreForm, StoreDetail } from "./StoreMaster";
import AdminListPage from "./AdminListPage";
import AdminClubs, { ClubForm } from "./AdminClubs";
import AdminDraws, { DrawForm } from "./AdminDraws";
import LuckyDrawResult from "../printcard/LuckyDrawResult";
import LuckyDrawWinner from "../printcard/LuckyDrawWinner";
import LaunchDraw from "./LaunchDraw";
import AdminTickets, { TicketForm, TicketDetail, TicketIssued } from "./AdminTickets";
import LaunchSettings from "./LaunchSettings";
import LaunchImages, { LaunchImageForm } from "./LaunchImages";
import WalletCharges from "./WalletCharges";
import PrintRequests from "../printcard/PrintRequests";
import PrintPhysicalCard from "../printcard/PhysicalLoyaltyCard";
import PrintPhysicalBack from "../printcard/PhysicalLoyaltyBack";
import PhysicalLoyaltyQr from "../printcard/PhysicalLoyaltyQr";
import StoreLoyaltySetting from "../store/LoyaltySetting";
import BusinessTypes from "./BusinessTypes";
import LocationMaster from "./LocationMaster";
import AuditTrail from "./AuditTrail";
import Communication, { CommunicationForm } from "./Communication";
import CommLogs from "./CommLogs";

const groups = [
  {
    label: "Overview",
    items: [{ to: "/console/home", label: "Dashboard", icon: SpaceDashboard }],
  },
  {
    label: "Masters",
    items: [
      { to: "/console/stores", label: "Stores", icon: Storefront },
      { to: "/console/business-types", label: "Business types", icon: Category },
      { to: "/console/locations", label: "States and cities", icon: Map },
      { to: "/console/clubs", label: "Clubs", icon: Groups },
    ],
  },
  {
    label: "OFFERS & ENGAGEMENT",
    items: [
      { to: "/console/coupons", label: "Coupons & Rewards", icon: LocalOffer },
      { to: "/console/luckydraw", label: "Lucky Draw Coupons", icon: EmojiEvents },
      { to: "/console/print-requests", label: "Print Loyalty Cards", icon: Print },
      { to: "/console/print-physical", label: "Print physical card", icon: CreditCard },
      { to: "/console/tickets", label: "Lucky Draw Tickets", icon: ConfirmationNumber },
      { to: "/console/qrcodes", label: "QR Coupons", icon: QrCode2 },
      { to: "/console/qrredeem", label: "QR Redeems", icon: QrCodeScanner },
    ],
  },
  {
    label: "Activity",
    items: [
      { to: "/console/customers", label: "Customers", icon: PeopleAlt },
      { to: "/console/issued", label: "Issued coupons", icon: ConfirmationNumber },
      { to: "/console/redeemed", label: "Redeemed coupons", icon: Redeem },
      { to: "/console/sales", label: "Sales", icon: PointOfSale },
      { to: "/console/cards", label: "Physical cards", icon: CreditCard },
      { to: "/console/reviews", label: "Reviews", icon: RateReview },
      { to: "/console/wallet", label: "Wallet", icon: AccountBalanceWallet },
      { to: "/console/audit", label: "Audit trail", icon: History },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/console/luckydraw/settings", label: "Lucky Draw Coupons", icon: Tune },
      { to: "/console/tickets/settings", label: "Lucky Draw Tickets", icon: Tune },
      { to: "/console/launch-images", label: "Launch images", icon: Image },
      { to: "/console/loyalty", label: "Loyalty settings", icon: Settings },
      { to: "/console/wallet/settings", label: "Wallet cuts", icon: AccountBalanceWallet },
      { to: "/console/communication", label: "Communication", icon: Campaign },
    ],
  },
];

const pageMeta = {
  "/console/home": ["Dashboard", "A snapshot of every store on Cashi"],
  "/console/stores": ["Stores", "Store name, owner, business, and status"],
  "/console/stores/new": ["Add store", "Create a store and choose its plan"],
  "/console/business-types": ["Business types", "Types used when a store is added"],
  "/console/locations": ["States and cities", "Cities belong to a state"],
  "/console/clubs": ["Clubs", "Group stores that share Lucky Draw Coupons"],
  "/console/clubs/new": ["Add club", "Code, title, and member stores"],
  "/console/coupons": ["Coupons", "Offers created by stores"],
  "/console/luckydraw": ["Lucky Draw Coupons", "Create coupons for a club or one store"],
  "/console/print-requests": ["Print Loyalty Cards", "Approve and track printed loyalty cards"],
  "/console/print-physical": ["Print physical card", "Print front, back, and QR"],
  "/console/print-physical-back": ["Print physical card back", "Back side of the loyalty card"],
  "/console/print-physical-qr": ["Print physical card QR", "QR codes for loyalty cards"],
  "/console/loyalty": ["Loyalty settings", "Default for every club and store"],
  "/console/luckydraw/settings": ["Lucky Draw Coupons Settings", "Launch timing, chance, and fixed rules"],
  "/console/launch-images": ["Launch images", "Shown on the left or right of the launch screen"],
  "/console/launch-images/new": ["Add launch image", "Name, image, draw, and left or right"],
  "/console/luckydraw/new": ["Create Lucky Draw Coupons", "Club or a single store"],
  "/console/luckydraw-winner": ["Lucky Draw Coupons Winner", "The customer picked for this draw"],
  "/console/tickets": ["Lucky Draw Ticket", "Sell a ticket with a link and QR"],
  "/console/tickets/settings": ["Lucky Draw Ticket Settings", "Launch timing, chance, and fixed rules"],
  "/console/tickets/new": ["Create Lucky Draw Ticket", "Price, quantity, and image"],
  "/console/customers": ["Customers", "Loyalty and Cashi Plus customers"],
  "/console/issued": ["Issued coupons", "Coupons handed to customers"],
  "/console/redeemed": ["Redeemed coupons", "Coupons already used"],
  "/console/sales": ["Sales", "Bills and loyalty points"],
  "/console/cards": ["Physical cards", "Issued loyalty and gift cards"],
  "/console/reviews": ["Reviews", "Customer feedback"],
  "/console/wallet": ["Wallet", "Store wallet movements"],
  "/console/wallet/settings": ["Wallet cuts", "Spin, coupon create, coupon issue, and print-card charges"],
  "/console/audit": ["Audit trail", "Every sale, wallet, coupon, and print movement"],
  "/console/communication": ["Communication", "Assign email, WhatsApp, SMS, and notification templates to events"],
  "/console/communication/new": ["Add template", "Tag an event and assign it to a customer or store owner"],
  "/console/communication/logs": ["Communication logs", "Sent, failed, and the reason"],
  "/console/qrcodes": ["QR coupons", "Generated, loaded, and activated codes"],
  "/console/qrredeem": ["QR redeems", "Codes customers have redeemed"],
};

const SIDEBAR = 252;

function SidebarContent({ name, onNavigate, onLogout }) {
  const location = useLocation();
  const initial = (name || "A").trim().charAt(0).toUpperCase();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", background: consoleColors.sidebar, color: "#fff" }}>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Typography sx={{ fontSize: 13, letterSpacing: 1.4, color: consoleColors.gold, fontWeight: 700 }}>CASHI</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, mt: 0.2 }}>Console</Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, pb: 2 }}>
        {groups.map((group) => (
          <Box key={group.label} sx={{ mb: 2 }}>
            <Typography sx={{ px: 1.2, mb: 0.6, fontSize: 11, letterSpacing: 0.8, color: consoleColors.navMuted, fontWeight: 700 }}>
              {group.label.toUpperCase()}
            </Typography>
            {group.items.map((item) => {
              const active = item.to === "/console/launch-images" || item.to === "/console/print-physical"
                ? location.pathname.startsWith(item.to)
                : location.pathname === item.to;
              const Icon = item.icon;
              return (
                <Box
                  key={item.to}
                  onClick={() => onNavigate(item.to)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 1.2,
                    py: 0.9,
                    mb: 0.3,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    color: active ? "#fff" : consoleColors.navText,
                    background: active ? consoleColors.navActive : "transparent",
                    "&:hover": { background: active ? consoleColors.navActive : "rgba(255,255,255,0.06)" },
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: active ? consoleColors.accent : consoleColors.navMuted }} />
                  <Typography sx={{ fontSize: 14, fontWeight: active ? 650 : 500 }}>{headingCase(item.label)}</Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 1.2 }}>
        <Avatar sx={{ width: 34, height: 34, bgcolor: consoleColors.accent, fontSize: 14 }}>{initial}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 650 }}>{name}</Typography>
          <Typography sx={{ fontSize: 12, color: consoleColors.navMuted }}>Super admin</Typography>
        </Box>
        <IconButton onClick={onLogout} size="small" sx={{ color: "#cbd5e1" }} aria-label="Logout">
          <Logout sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export default function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminData, setAdminData } = useContext(DataContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const name = adminData?.admin_info?.name || "Super Admin";
  const [title, subtitle] = pageMeta[location.pathname]
    || (location.pathname.startsWith("/console/launch-images/") && location.pathname.endsWith("/edit") ? ["Edit launch image", "Name, image, draw, and left or right"] : null)
    || (location.pathname.startsWith("/console/luckydraw/") && location.pathname.endsWith("/edit") ? ["Edit Lucky Draw Coupons", "Update title, schedule, and images"] : null)
    || (location.pathname.startsWith("/console/luckydraw/") ? ["Lucky Draw Coupons", "Result, issued customers, and winners"] : null)
    || (location.pathname.startsWith("/console/tickets/") && location.pathname.endsWith("/edit") ? ["Edit Lucky Draw Ticket", "Price, quantity, and image"] : null)
    || (location.pathname.startsWith("/console/tickets/") && location.pathname.endsWith("/issued") ? ["Lucky Draw Ticket", "Issued customers and winners"] : null)
    || (location.pathname.startsWith("/console/tickets/") && location.pathname.includes("/launch") ? ["Launch Draw", "Countdown, then the winner"] : null)
    || (location.pathname.startsWith("/console/tickets/") ? ["Lucky Draw Ticket", "Link, QR, and payments"] : null)
    || (location.pathname.startsWith("/console/stores/") && location.pathname.endsWith("/edit") ? ["Edit store", "Update details, plan, and approval"] : null)
    || (location.pathname.startsWith("/console/stores/") ? ["Store", "Full store details"] : null)
    || (location.pathname.startsWith("/console/communication/") && location.pathname.endsWith("/edit") ? ["Edit template", "Tag an event and assign it to a customer or store owner"] : null)
    || (location.pathname.includes("/edit") ? ["Edit club", "Code, title, and member stores"] : ["Console", ""]);

  const logout = () => {
    setAdminData({});
    sessionStorage.removeItem("adminData");
    navigate("/console", { replace: true });
  };

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: consoleColors.page }}>
      <Box sx={{ width: SIDEBAR, flexShrink: 0, display: { xs: "none", md: "block" } }}>
        <Box sx={{ position: "fixed", width: SIDEBAR, height: "100vh" }}>
          <SidebarContent name={name} onNavigate={go} onLogout={logout} />
        </Box>
      </Box>

      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { md: "none" }, "& .MuiDrawer-paper": { width: SIDEBAR, border: 0 } }}>
        <SidebarContent name={name} onNavigate={go} onLogout={logout} />
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ position: "sticky", top: 0, zIndex: 10, background: consoleColors.pageBar, backdropFilter: "blur(8px)", borderBottom: `1px solid ${consoleColors.line}`, px: { xs: 2, md: 3 }, py: 1.6, display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => setMobileOpen(true)} sx={{ display: { md: "none" } }} aria-label="Open menu">
            <Menu />
          </IconButton>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: consoleColors.ink, lineHeight: 1.2 }}>{headingCase(title)}</Typography>
            <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{headingCase(subtitle)}</Typography>
          </Box>
        </Box>

        <Routes>
          <Route path="home" element={<AdminHome />} />
          <Route path="stores/new" element={<StoreForm />} />
          <Route path="stores/:storeId/edit" element={<StoreForm />} />
          <Route path="stores/:storeId" element={<StoreDetail />} />
          <Route path="stores" element={<StoreMaster />} />
          <Route path="business-types" element={<BusinessTypes />} />
          <Route path="locations" element={<LocationMaster />} />
          <Route path="coupons" element={<AdminListPage title="Coupons" mod="ADMIN_COUPON_LIST" kind="coupons" />} />
          <Route path="clubs/new" element={<ClubForm />} />
          <Route path="clubs/:clubId/edit" element={<ClubForm />} />
          <Route path="clubs" element={<AdminClubs />} />
          <Route path="luckydraw-winner" element={<LuckyDrawWinner />} />
          <Route path="luckydraw/settings" element={<LaunchSettings kind="coupon" />} />
          <Route path="launch-images/new" element={<LaunchImageForm />} />
          <Route path="launch-images/:imageId/edit" element={<LaunchImageForm />} />
          <Route path="launch-images" element={<LaunchImages />} />
          <Route path="luckydraw/:drawId/launch" element={<LaunchDraw />} />
          <Route path="luckydraw/new" element={<DrawForm />} />
          <Route path="luckydraw/:drawId/edit" element={<DrawForm />} />
          <Route path="tickets/settings" element={<LaunchSettings kind="ticket" />} />
          <Route path="tickets/new" element={<TicketForm />} />
          <Route path="tickets/:ticketId/edit" element={<TicketForm />} />
          <Route path="tickets/:ticketId/launch" element={<LaunchDraw kind="ticket" />} />
          <Route path="tickets/:ticketId/issued" element={<TicketIssued />} />
          <Route path="tickets/:ticketId" element={<TicketDetail />} />
          <Route path="tickets" element={<AdminTickets />} />
          <Route path="print-requests" element={<PrintRequests source="admin" />} />
          <Route path="print-physical" element={<PrintPhysicalCard />} />
          <Route path="print-physical-back" element={<PrintPhysicalBack />} />
          <Route path="print-physical-qr" element={<PhysicalLoyaltyQr />} />
          <Route path="luckydraw" element={<AdminDraws />} />
          <Route path="luckydraw/:drawId" element={<LuckyDrawResult />} />
          <Route path="customers" element={<AdminListPage title="Customers" mod="ADMIN_CUSTOMER_LIST" kind="customers" />} />
          <Route path="issued" element={<AdminListPage title="Issued coupons" mod="ADMIN_ISSUED_LIST" kind="issued" />} />
          <Route path="redeemed" element={<AdminListPage title="Redeemed coupons" mod="ADMIN_REDEEM_LIST" kind="redeemed" />} />
          <Route path="sales" element={<AdminListPage title="Sales" mod="ADMIN_SALE_LIST" kind="sales" />} />
          <Route path="cards" element={<AdminListPage title="Physical cards" mod="ADMIN_CARD_LIST" kind="cards" />} />
          <Route path="reviews" element={<AdminListPage title="Reviews" mod="ADMIN_REVIEW_LIST" kind="reviews" />} />
          <Route path="loyalty" element={<StoreLoyaltySetting source="admin" />} />
          <Route path="wallet/settings" element={<WalletCharges />} />
          <Route path="wallet" element={<AdminListPage title="Wallet" mod="ADMIN_WALLET_LIST" kind="wallet" />} />
          <Route path="audit" element={<AuditTrail />} />
          <Route path="communication/new" element={<CommunicationForm />} />
          <Route path="communication/:templateId/edit" element={<CommunicationForm />} />
          <Route path="communication/logs" element={<CommLogs />} />
          <Route path="communication" element={<Communication />} />
          <Route path="qrcodes" element={<AdminListPage title="QR coupons" mod="ADMIN_QR_LIST" kind="qrcodes" />} />
          <Route path="qrredeem" element={<AdminListPage title="QR redeems" mod="ADMIN_QR_LIST" kind="qrredeem" extra={{ redeem_status: "1" }} />} />
          <Route path="*" element={<Navigate to="/console/home" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}
